import React, { ChangeEvent, useEffect, useState } from 'react'

import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import FormSection from 'components_new/molecules/FormSection'
import SearchAndSelect from 'components_new/molecules/SearchAndSelect'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import {
  useOrganizationalUnitById,
  useOrganizationalUnitsArray
} from 'redux/hooks/OrganizationalUnits'
import { useOrganizationalUnitActions } from 'redux/actions/OrganizationalUnits'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import {
  useAvailableUnits,
  useAvailableUnitsFetched
} from 'redux/hooks/AvailableUnits'

interface MyOrgUnitsDialogProps {
  id: string
  onClose: () => void
  open: boolean
}

const EditDataAccessGroupDialog = React.forwardRef(
  (props: MyOrgUnitsDialogProps, ref) => {
    const { id, onClose, open } = props

    const { t } = useTranslation()
    const { tryUpdateOrganizationalUnit } = useOrganizationalUnitActions()

    const organizationalUnit = useOrganizationalUnitById(id)
    const organizationalUnits = useOrganizationalUnitsArray()
    const { isCompanyBased } = useAccountSettings()
    const availableUnits = useAvailableUnits()
    const availableUnitsFetched = useAvailableUnitsFetched()

    const [name, setName] = useState('')
    const [selectedNames, setSelectedNames] = useState<string[]>([])

    useEffect(() => {
      if (organizationalUnit) {
        setName(organizationalUnit.name)
        setSelectedNames(organizationalUnit.names)
      }
    }, [organizationalUnit, open])

    const handleSave = async () => {
      try {
        await tryUpdateOrganizationalUnit(id, {
          name,
          names: selectedNames
        })
        onClose()
      } catch (error) {
        console.error('Failed to update organizational unit:', error)
      }
    }

    const handleClose = () => {
      onClose()
    }

    const handleChangeName = (e: ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value)
    }

    const handleChangeSelectedNames = (value: string[]) => {
      setSelectedNames(value)
    }

    // Check if name is empty or duplicate
    const isDuplicateName = organizationalUnits.some(
      (unit) => unit.id !== id && unit.name.trim() === name.trim()
    )
    const isNameEmpty = name.trim().length === 0
    const isSaveDisabled = isNameEmpty || isDuplicateName

    /*-- default --*/
    return (
      <SimpleDialog
        actions={
          <>
            <Button onClick={handleClose} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button disabled={isSaveDisabled} onClick={handleSave}>
              {t('global', 'save')}
            </Button>
          </>
        }
        hasClose={true}
        helperText={t(
          'data_access',
          isCompanyBased ? 'helper_text_company' : 'helper_text'
        )}
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        ref={ref}
        title={`${t('global', 'edit')} ${t(
          'data_access',
          'data_access',
          true
        )}`}
      >
        <FormSection
          disableMargin={true}
          importance="required"
          sx={{ mb: 1 }}
          title={t('global', 'title')}
        >
          <TextField
            error={isDuplicateName}
            helperText={
              isDuplicateName ? t('global', 'duplicate_name_error') : undefined
            }
            onChange={handleChangeName}
            placeholder={t('data_access', 'title_placeholder')}
            value={name}
          />
        </FormSection>
        <FormSection
          disableMargin={true}
          sx={{ mb: 1 }}
          title={t('org_units', isCompanyBased ? 'units_company' : 'units')}
        >
          <SearchAndSelect
            autoFocus={true}
            loading={!availableUnitsFetched}
            options={availableUnits.map((unit) => {
              return { id: unit, title: unit }
            })}
            onChange={handleChangeSelectedNames}
            value={selectedNames}
          />
        </FormSection>
      </SimpleDialog>
    )
  }
)

export default EditDataAccessGroupDialog
